import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Mail, Loader2, Paperclip } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

/**
 * Bottone "Invia email al cliente" + dialog compose.
 * Riutilizza SMTP già configurato. Registra invio come evento nel timeline.
 * Precompila destinatario/oggetto/corpo con dati ticket + lingua cliente.
 */
export function EmailComposeButton({ ticket, attachments = [], onSent, className = "" }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ to: "", subject: "", body: "" });
  const [selectedAttachments, setSelectedAttachments] = useState({});

  const defaultTo =
    ticket?.dispenser?.end_customer_email ||
    ticket?.customer?.email ||
    ticket?.dispenser?.customer_email ||
    "";

  useEffect(() => {
    if (!open) return;
    const lang = (ticket?.customer_language || "it").toLowerCase();
    const isEn = lang === "en";
    const num = ticket?.ticket_number || "";
    const title = ticket?.title || "";
    const subject = isEn
      ? `[Ticket #${num}] ${title}`
      : `[Ticket #${num}] ${title}`;
    const greeting = isEn ? "Hello," : "Buongiorno,";
    const closing = isEn ? "Best regards,\nWineemotion Technical Support" : "Cordiali saluti,\nAssistenza tecnica Wineemotion";
    const body = `${greeting}\n\n\n\n${closing}`;
    setForm({ to: defaultTo, subject, body });
    setSelectedAttachments({});
  }, [open, ticket?.ticket_number, ticket?.title, ticket?.customer_language, defaultTo]);

  const send = async () => {
    if (!form.to.trim() || !form.to.includes("@")) { toast.error("Destinatario email non valido"); return; }
    if (!form.subject.trim()) { toast.error("Oggetto obbligatorio"); return; }
    if (!form.body.trim()) { toast.error("Corpo email obbligatorio"); return; }
    setBusy(true);
    try {
      const attachment_ids = Object.entries(selectedAttachments).filter(([, v]) => v).map(([id]) => id);
      const { data } = await api.post(`/tickets/${ticket.id}/email`, {
        to: form.to.trim(),
        subject: form.subject.trim(),
        body: form.body,
        attachment_ids,
      });
      toast.success(`Email inviata a ${form.to}${data.attachments_sent ? ` (${data.attachments_sent} allegati)` : ""}`);
      setOpen(false);
      if (onSent) await onSent();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const toggleAttachment = (id) => setSelectedAttachments((s) => ({ ...s, [id]: !s[id] }));

  return (
    <>
      <Button
        onClick={() => setOpen(true)}
        size="sm"
        variant="outline"
        data-testid="email-compose-btn"
        className={`rounded-sm ${className}`}
      >
        <Mail className="h-3.5 w-3.5 mr-1.5" /> Invia email
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm max-w-lg" data-testid="email-compose-dialog">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">Invia email al cliente</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-500">Destinatario *</Label>
              <Input
                data-testid="email-to"
                type="email"
                value={form.to}
                onChange={(e) => setForm({ ...form, to: e.target.value })}
                placeholder="cliente@esempio.com"
                className="mt-1 rounded-sm"
              />
              {!defaultTo && (
                <div className="mt-1 text-[11px] text-amber-800">
                  Nessuna email cliente in anagrafica — inserisci il destinatario manualmente.
                </div>
              )}
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-500">Oggetto *</Label>
              <Input
                data-testid="email-subject"
                value={form.subject}
                onChange={(e) => setForm({ ...form, subject: e.target.value })}
                className="mt-1 rounded-sm"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-500">Messaggio *</Label>
              <Textarea
                data-testid="email-body"
                rows={8}
                value={form.body}
                onChange={(e) => setForm({ ...form, body: e.target.value })}
                className="mt-1 rounded-sm font-mono text-sm"
              />
            </div>
            {attachments && attachments.length > 0 && (
              <div>
                <Label className="text-xs uppercase tracking-widest text-zinc-500 mb-2 block">
                  <Paperclip className="h-3 w-3 inline mr-1" /> Allega file del ticket
                </Label>
                <div className="rounded-sm border border-zinc-200 max-h-32 overflow-y-auto">
                  {attachments.map((a) => (
                    <label
                      key={a.id}
                      className="flex items-center gap-2 px-3 py-2 hover:bg-zinc-50 border-b border-zinc-100 last:border-b-0 cursor-pointer text-xs"
                      data-testid={`email-attach-${a.id}`}
                    >
                      <Checkbox
                        checked={!!selectedAttachments[a.id]}
                        onCheckedChange={() => toggleAttachment(a.id)}
                      />
                      <span className="flex-1 truncate">{a.original_filename}</span>
                      <span className="text-zinc-500 text-[10px] shrink-0">{a.content_type}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setOpen(false)} disabled={busy}>Annulla</Button>
            <Button
              data-testid="email-send"
              className="bg-brand hover:bg-brand-dark text-white rounded-sm"
              onClick={send}
              disabled={busy}
            >
              {busy ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Invio...</> : <><Mail className="h-4 w-4 mr-2" /> Invia email</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
